/**
 * XML Document Import Cache
 * Manages template cache and block index (filename metadata) for efficient document imports
 */

import { getComplexDocument } from './services/complexDocumentsAPI';
import { fetchPublishedBlocksByMetadataFilter } from './services/complexDocumentsAPI';

// Template cache: Map<language, templateJSON>
const templateCache = new Map();

// Block index: Map<povc-filename, Map<language, blockData>> (filename = basename incl. .slb, from metadata)
const filenameBlockIndex = new Map();

// Cache initialization state
let isInitialized = false;
let initializationError = null;

/**
 * Read a string value from block.metadata (plain object). Checks direct keys, then
 * nested metaTags / metadata arrays when present on that object.
 * @param {Object} block
 * @param {string} key
 * @returns {string|null}
 */
function getMetaTagValue(block, key) {
  const meta = block.metadata;
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) {
    return null;
  }

  const direct = meta[key];
  if (direct != null && String(direct).trim() !== '') {
    return String(direct).trim();
  }

  if (Array.isArray(meta.metaTags)) {
    const tag = meta.metaTags.find((t) => t.key === key);
    if (tag && tag.value != null && String(tag.value).trim() !== '') {
      return String(tag.value).trim();
    }
  }

  if (Array.isArray(meta.metadata)) {
    const tag = meta.metadata.find((t) => t.key === key);
    if (tag && tag.value != null && String(tag.value).trim() !== '') {
      return String(tag.value).trim();
    }
  }

  return null;
}

/**
 * Initialize template cache by fetching templates for each language
 * Only fetches templates for languages that have provided IDs
 * @param {Object} templateIds - Object with language keys and communicationId values
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<void>}
 */
export const initializeTemplateCache = async (templateIds, onProgress) => {
  try {
    templateCache.clear();
    
    const languages = ['fr', 'nl', 'en', 'de'];
    
    // Filter to only languages with provided IDs
    const languagesToFetch = languages.filter(lang => {
      const id = templateIds[lang];
      return id && id.trim() !== '';
    });
    
    if (languagesToFetch.length === 0) {
      // No templates to fetch, but this is allowed
      if (onProgress) {
        onProgress({
          stage: 'templates-complete',
          templateCount: 0,
        });
      }
      return;
    }
    
    for (let i = 0; i < languagesToFetch.length; i++) {
      const language = languagesToFetch[i];
      const communicationId = templateIds[language];
      
      if (onProgress) {
        onProgress({
          stage: 'fetching-templates',
          language,
          progress: i + 1,
          total: languagesToFetch.length,
        });
      }
      
      const response = await getComplexDocument(communicationId);
      
      if (response.data) {
        templateCache.set(language, response.data);
      } else {
        throw new Error(`Failed to fetch template for language ${language}: ${response.statusCode || 'Unknown error'}`);
      }
    }
    
    if (onProgress) {
      onProgress({
        stage: 'templates-complete',
        templateCount: templateCache.size,
      });
    }
  } catch (error) {
    console.error('Error initializing template cache:', error);
    initializationError = error;
    throw error;
  }
};

/**
 * Initialize block index by fetching published paragraph blocks and indexing by filename metadata
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<void>}
 */
export const initializeBlockIndex = async (onProgress) => {
  try {
    filenameBlockIndex.clear();
    
    if (onProgress) {
      onProgress({
        stage: 'fetching-blocks',
        message: 'Fetching all published blocks for filename metadata index...',
      });
    }
    
    // Fetch blocks using the new API with metadata filter
    // Criteria: filter for reusable blocks (type: paragraph)
    // Languages: empty string to fetch all languages
    const criteria = [{ key: "type", value: "paragraph" }];
    const languages = ""; // Empty string to fetch all languages
    const page = 0;
    const size = 20000; // Fetch all blocks just in case
    
    const blocks = await fetchPublishedBlocksByMetadataFilter(
      criteria,
      "",
      languages,
      page,
      size,
      (progress) => {
        if (onProgress) {
          onProgress({
            stage: 'fetching-blocks',
            page: progress.page,
            totalFetched: progress.totalFetched,
          });
        }
      }
    );
    
    for (const block of blocks) {
      const language =
        getMetaTagValue(block, 'language') || block.language || 'en';

      const filenameValue = getMetaTagValue(block, 'filename');
      const povcValue = getMetaTagValue(block, 'povc') || '';

      if (filenameValue) {
        const indexKey = `${povcValue}-${filenameValue}`;
        if (!filenameBlockIndex.has(indexKey)) {
          filenameBlockIndex.set(indexKey, new Map());
        }
        filenameBlockIndex.get(indexKey).set(language, block);
      }
    }
    
    if (onProgress) {
      onProgress({
        stage: 'blocks-complete',
        blockCount: blocks.length,
        indexedCount: filenameBlockIndex.size,
      });
    }
  } catch (error) {
    console.error('Error initializing block index:', error);
    initializationError = error;
    throw error;
  }
};

/**
 * Initialize both template cache and block index
 * @param {Object} templateIds - Object with language keys and communicationId values
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<void>}
 */
export const initializeCache = async (templateIds, onProgress) => {
  try {
    isInitialized = false;
    initializationError = null;
    
    if (onProgress) {
      onProgress({
        stage: 'initializing',
        message: 'Starting cache initialization...',
      });
    }
    
    // Initialize templates first (only for languages with provided IDs)
    await initializeTemplateCache(templateIds || {}, onProgress);
    
    // Then initialize block index (only if using referenced blocks)
    await initializeBlockIndex(onProgress);
    
    isInitialized = true;
    
    if (onProgress) {
      onProgress({
        stage: 'complete',
        message: 'Cache initialization complete',
        templateCount: templateCache.size,
        blockIndexCount: filenameBlockIndex.size,
      });
    }
  } catch (error) {
    isInitialized = false;
    initializationError = error;
    if (onProgress) {
      onProgress({
        stage: 'error',
        error: error.message,
      });
    }
    throw error;
  }
};

/**
 * Get cached template for a specific language (deep cloned)
 * @param {string} language - Language code (fr, nl, en, de)
 * @returns {Object|null} Deep cloned template JSON or null if not found
 */
export const getTemplate = (language) => {
  const template = templateCache.get(language);
  if (!template) {
    return null;
  }
  // Deep clone to avoid mutating the cached template
  return JSON.parse(JSON.stringify(template));
};

/**
 * Lookup block by povc, filename (basename including .slb) and language — matches filename metadata on published blocks
 * @param {string} povc - povc value to lookup
 * @param {string} filenameWithExtension - filename basename e.g. -_1.slb
 * @param {string} language - Language code (fr, nl, en, de)
 * @returns {Object|null} Block data or null if not found
 */
export const lookupBlockByFilename = (povc = '', filenameWithExtension, language) => {
  if (!filenameWithExtension || String(filenameWithExtension).trim() === '') {
    return null;
  }
  
  const indexKey = `${povc}-${String(filenameWithExtension).trim()}`;
  const languageMap = filenameBlockIndex.get(indexKey);
  if (!languageMap) {
    return null;
  }
  
  return languageMap.get(language) || null;
};

/**
 * Check if cache is initialized
 * @returns {boolean}
 */
export const isCacheInitialized = () => {
  return isInitialized;
};

/**
 * Get cache status
 * @returns {Object} Cache status information
 */
export const getCacheStatus = () => {
  return {
    isInitialized,
    initializationError: initializationError?.message || null,
    templateCount: templateCache.size,
    blockIndexCount: filenameBlockIndex.size,
    availableLanguages: Array.from(templateCache.keys()),
  };
};

/**
 * Clear all caches
 */
export const clearCache = () => {
  templateCache.clear();
  filenameBlockIndex.clear();
  isInitialized = false;
  initializationError = null;
};

